import { AlertCircle, AlertTriangle, Ban, Info } from 'lucide-react';

import { Typography } from '@/components/ui/typography';
import { IconComponent } from '../../../../util/icons';
import visaIcon from '../../../../asset/images/visa.png';
import amexIcon from '../../../../asset/images/amex.svg';
import discoverIcon from '../../../../asset/images/discover.png';
import masterCard from '../../../../asset/icons/masterIcon.svg';
import { CardType } from '../../../../types/order.types';

// ---------------------------------------------------------------------------
// Card type icon — brand assets stay as raster/SVG since they're trademarked.
// ---------------------------------------------------------------------------

export function CardTypeIcon({ type }: { type: string }) {
  const upper = type?.toUpperCase();
  if (upper === CardType.VISA)
    return <IconComponent src={visaIcon} alt="Visa" width={40} height={12} />;
  if (upper === CardType.MASTERCARD)
    return <IconComponent src={masterCard} alt="Mastercard" />;
  if (upper === CardType.AMEX)
    return <IconComponent src={amexIcon} alt="American Express" width={48} height={27} />;
  if (upper === CardType.DISCOVER)
    return <IconComponent src={discoverIcon} alt="Discover" width={48} height={26} />;
  return null;
}

export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ---------------------------------------------------------------------------
// Banner sub-components
// ---------------------------------------------------------------------------

export function EnterpriseBanner() {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md bg-info-soft p-3">
      <Info
        className="mt-0.5 h-5 w-5 shrink-0 text-info-soft-foreground"
        aria-hidden="true"
      />
      <Typography variant="body">
        You are on a <span className="font-semibold">monthly payment plan</span>.
        This order will be added to your next monthly invoice.
      </Typography>
    </div>
  );
}

export function BulkOrderHeader({
  fileNameInfo,
  orderCount,
  failedCountInfo,
  hasCards,
}: {
  fileNameInfo?: string | null;
  orderCount?: string | number;
  failedCountInfo?: string | null;
  hasCards: boolean;
}) {
  const failedCount = Number(failedCountInfo ?? 0);
  return (
    <div className="mb-4">
      <Typography as="h2" variant="body-semibold">
        Order details
      </Typography>
      <div className="mt-1 flex items-center gap-4 text-muted-foreground">
        <Typography variant="body">{fileNameInfo}</Typography>
        <Typography variant="body">
          {hasCards ? `${orderCount} accepted` : `${orderCount} valid orders`}
        </Typography>
        {!hasCards && failedCount > 0 && (
          <span className="flex items-center gap-1 text-destructive">
            <Ban className="h-4 w-4" aria-hidden="true" />
            <Typography variant="body">
              {failedCountInfo} invalid orders
            </Typography>
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card status badge — used on saved card rows
// ---------------------------------------------------------------------------

interface CardStatusBadgeProps {
  status: 'expiringSoon' | 'expired' | 'default';
}

export function CardStatusBadge({ status }: CardStatusBadgeProps) {
  if (status === 'expiringSoon') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-warning-soft-foreground">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
        <Typography variant="small">Expiring Soon</Typography>
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive-soft px-2 py-0.5 text-destructive">
        <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
        <Typography variant="small">Expired</Typography>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-success">
      <Typography variant="small">Default card</Typography>
    </span>
  );
}
